/**
 * ConditionFilterDropdown - Card condition filter built on the generic FilterDropdown.
 *
 * Renders condition grades (Mint through Damaged) as a searchable multi-select dropdown.
 * No icons — condition grades are best conveyed by their text labels.
 */
import type { ReactElement } from 'react';

import type { FilterDropdownOption } from '@/components/FilterDropdown/FilterDropdown';
import FilterDropdown from '@/components/FilterDropdown/FilterDropdown';
import type { Condition } from '@/types/card';

export interface ConditionFilterDropdownProps {
  selectedConditions: Condition[];
  onToggle: (condition: Condition) => void;
  onClear: () => void;
  className?: string;
}

const CONDITION_OPTIONS: FilterDropdownOption[] = [
  { value: 'M', label: 'Mint', badgeClassName: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'NM', label: 'Near Mint', badgeClassName: 'bg-green-500/20 text-green-400' },
  { value: 'LP', label: 'Lightly Played', badgeClassName: 'bg-lime-500/20 text-lime-400' },
  { value: 'MP', label: 'Moderately Played', badgeClassName: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'HP', label: 'Heavily Played', badgeClassName: 'bg-orange-500/20 text-orange-400' },
  { value: 'D', label: 'Damaged', badgeClassName: 'bg-red-500/20 text-red-400' },
];

export default function ConditionFilterDropdown({
  selectedConditions,
  onToggle,
  onClear,
  className,
}: ConditionFilterDropdownProps): ReactElement {
  return (
    <FilterDropdown
      triggerLabel="Condition"
      ariaLabel="Condition filter"
      searchPlaceholder="Search conditions..."
      options={CONDITION_OPTIONS}
      selectedValues={selectedConditions}
      onToggle={(value) => onToggle(value as Condition)}
      onClear={onClear}
      className={className}
    />
  );
}
